import { useEffect, useState } from 'react';
import { Search, ShieldCheck, Compass, CircleAlert, X } from 'lucide-react';
import { Notice } from '@kollez/design-system';
import { useTenantSearch, type TenantPickerHit } from '@/lib/queries';

/**
 * TenantPicker (PR 4b) — single-search-box tenant picker for the register-student wizard.
 *
 * Search runs against GET /public/tenants/search?q= (PR 4a endpoint). Hits are grouped into
 *   - "On Kollez" (source: 'kollez') — verified, immediate onboarding.
 *   - "Not on Kollez yet" (source: 'aishe') — AISHE master fallback for cold declare.
 *
 * On selection, calls onPick with either a `tenantId` (Kollez source) or an `aisheCollegeId`
 * (AISHE source) alongside the display name. The parent wizard decides which to send in the
 * register payload. Cold-declare colleges surface a Notice explaining the degraded experience.
 */

export interface TenantPickerValue {
  source: 'kollez' | 'aishe';
  tenantId?: string;
  aisheCollegeId?: string;
  name: string;
  breadcrumb?: string;
  tenantKind?: string;
  city?: string;
  district?: string;
  state?: string;
}

interface TenantPickerProps {
  value: TenantPickerValue | null;
  onPick: (value: TenantPickerValue | null) => void;
}

// Simple debounced value hook — 250ms.
function useDebounced<T>(value: T, ms = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function TenantPicker({ value, onPick }: TenantPickerProps) {
  const [q, setQ] = useState('');
  const debouncedQ = useDebounced(q, 250);
  const search = useTenantSearch(debouncedQ);

  // Once picked, the search field is replaced by a summary card.
  if (value) {
    return (
      <div className="grid gap-3">
        <div className="card card-pad flex gap-4 items-start">
          <span
            className="logo-tile"
            style={{
              background: value.source === 'kollez' ? 'rgb(var(--bg-inkwell))' : 'rgb(var(--bg-tertiary))',
              color: value.source === 'kollez' ? '#fff' : 'rgb(var(--ink-primary))',
              border: 0,
              width: 44,
              height: 44,
            }}
          >
            {value.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-semibold">{value.name}</div>
            {value.breadcrumb && (
              <div className="text-sm text-ink-secondary truncate">{value.breadcrumb}</div>
            )}
            {(value.city || value.district || value.state) && (
              <div className="text-sm text-ink-secondary">
                {[value.city, value.district, value.state].filter(Boolean).join(' · ')}
              </div>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {value.source === 'kollez' ? (
                <span className="badge badge-success"><ShieldCheck size={12} />Verified on Kollez</span>
              ) : (
                <span className="badge"><Compass size={12} />AISHE listing · not on Kollez yet</span>
              )}
              {value.tenantKind && <span className="badge">{value.tenantKind}</span>}
            </div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => { onPick(null); setQ(''); }}
            aria-label="Change college"
          >
            <X size={14} /> Change
          </button>
        </div>
        {value.source === 'aishe' && (
          <Notice tone="info">
            Your college isn&apos;t on Kollez yet. You can use Kollez Public — public catalog, prep,
            resume builder, career advisor — while we get your college onboarded. We&apos;ll notify
            you when they join.
          </Notice>
        )}
      </div>
    );
  }

  // Search UI.
  const hits = search.data?.hits ?? [];
  const kollezHits = hits.filter((h) => h.source === 'kollez');
  const aisheHits = hits.filter((h) => h.source === 'aishe');
  const showingResults = debouncedQ.trim().length >= 2 && (search.isSuccess || search.isFetching);

  return (
    <div className="grid gap-3">
      <div className="input-group">
        <Search size={14} />
        <input
          className="input"
          type="search"
          placeholder="Type your college, university, or education group name"
          autoComplete="off"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>
      {q.trim().length > 0 && q.trim().length < 2 && (
        <p className="helper">Keep typing — at least 2 characters.</p>
      )}
      {showingResults && (
        <div className="card" style={{ maxHeight: 380, overflowY: 'auto' }}>
          {search.isFetching && (
            <div className="p-4 text-sm text-ink-secondary">Searching…</div>
          )}
          {!search.isFetching && hits.length === 0 && (
            <div className="p-4">
              <p className="text-sm mb-1"><CircleAlert size={14} className="inline mr-1" />No matches for &ldquo;{debouncedQ}&rdquo;.</p>
              <p className="helper m-0">
                Try the local name of your college, its city, or its acronym. If it&apos;s not
                listed at all, pick &ldquo;Not affiliated&rdquo; above and add your college later.
              </p>
            </div>
          )}
          {kollezHits.length > 0 && (
            <div className="grid gap-1 p-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-tertiary px-2 pt-2 pb-1">On Kollez</div>
              {kollezHits.map((hit) => (
                <HitRow key={`k-${hit.tenantId}`} hit={hit} onPick={onPick} />
              ))}
            </div>
          )}
          {aisheHits.length > 0 && (
            <div className="grid gap-1 p-2 border-t border-line-tertiary">
              <div className="text-xs font-semibold uppercase tracking-wider text-ink-tertiary px-2 pt-2 pb-1">
                Not on Kollez yet · AISHE listings
              </div>
              {aisheHits.map((hit) => (
                <HitRow key={`a-${hit.aisheCollegeId}`} hit={hit} onPick={onPick} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HitRow({ hit, onPick }: { hit: TenantPickerHit; onPick: (v: TenantPickerValue) => void }) {
  return (
    <button
      type="button"
      className="w-full text-left rounded-md px-2 py-2 hover:bg-tertiary focus:bg-tertiary focus:outline-none"
      onClick={() =>
        onPick({
          source: hit.source,
          tenantId: hit.tenantId,
          aisheCollegeId: hit.aisheCollegeId,
          name: hit.name,
          breadcrumb: hit.breadcrumb,
          tenantKind: hit.tenantKind,
          city: hit.city,
          district: hit.district,
          state: hit.state,
        })
      }
    >
      <div className="font-medium">{hit.name}</div>
      {hit.breadcrumb && (
        <div className="text-xs text-ink-secondary truncate">{hit.breadcrumb}</div>
      )}
      {(hit.city || hit.district || hit.state) && (
        <div className="text-xs text-ink-tertiary">
          {[hit.city, hit.district, hit.state].filter(Boolean).join(' · ')}
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-1">
        {hit.source === 'kollez' ? (
          <span className="badge badge-success" style={{ fontSize: 10 }}><ShieldCheck size={10} />Verified</span>
        ) : (
          <span className="badge" style={{ fontSize: 10 }}>AISHE listing</span>
        )}
        {hit.tenantKind && <span className="badge" style={{ fontSize: 10 }}>{hit.tenantKind}</span>}
      </div>
    </button>
  );
}
